import React, { useState, useEffect } from "react";
import StripeImage from "../../../src/assets/stripe.png";
import PaypalImage from "../../../src/assets/paypal.png";
import axios from "axios";
import moment from "moment";
import { useCart } from "../Event/CartContext";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import Loader from "../common/loader";

const PaymentModal = ({ setStraper, onClose }) => {
  const { cart, dispatch } = useCart();
  const [tax, setTax] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [selectedPayment, setSelectedPayment] = useState("");
  const [totalPrice, setTotalPrice] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    calculateTotalPrice();
  }, [cart]);

  useEffect(() => {
    calculateGrandTotal();
  }, [taxAmount]);

  const handlePaymentChange = (method) => {
    setSelectedPayment(method);
  };

  const calculateTotalPrice = () => {
    let total = 0;
    let calculatedTax = 0;
    cart.forEach((item) => {
      total += item.course.price * item.seats;
      const courseTax = parseFloat(item?.course?.tax || 5);
      calculatedTax += item.course.price * item.seats * (courseTax / 100);
    });
    setTaxAmount(calculatedTax);
    setTotalPrice(total);
  };

  const calculateGrandTotal = () => {
    const value = totalPrice + taxAmount;
    setGrandTotal(value);
  };

  const getInitialFormData = (cart) => {
    return cart.reduce((acc, item) => {
      const savedFormData = JSON.parse(
        localStorage.getItem(`attendees_${item.event._id}`),
      );
      acc[item.event._id] = savedFormData || Array(item.seats).fill({});
      return acc;
    }, {});
  };

  const addAttendee = async (initialFormData, dispatch, paymentAmount) => {
    try {
      const addeventIdentifier = uuidv4();
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/event/addAttendees`,
        {
          attendees: initialFormData,
          addeventIdentifier,
          paymentAmount,
        },
      );
      console.log("Attendees added successfully:", response.data);
      await dispatch({ type: "CLEAR_CART" });
      localStorage.clear();

      const getFirstEmail = (obj) => {
        for (const key in obj) {
          const arr = obj[key];
          if (Array.isArray(arr) && arr.length > 0 && arr[0].email) {
            return arr[0].email;
          }
        }
        return null;
      };
      const mail = getFirstEmail(initialFormData);
      localStorage.setItem("mail_for_get_invoice", mail);
      return response.data;
    } catch (error) {
      console.error("Error adding the attendees!", error);
      toast.error(
        "There was an error adding the attendees, please try again later.",
      );
    }
  };

  function transformData(data, addeventIdentifier) {
    const result = [];

    for (const [eventId, attendees] of Object.entries(data)) {
      const newAttendeesEmails =
        attendees?.newAttendees?.map((attendee) => attendee.email) || [];
      const existingAttendeesEmails =
        attendees?.existingAttendees?.map((attendee) => attendee.email) || [];
      const attendeesEmail = Array.from(
        new Set([...newAttendeesEmails, ...existingAttendeesEmails]),
      );

      const billingEmail = attendees?.newAttendees?.[0]?.billingEmail
        ? [attendees.newAttendees[0].billingEmail]
        : attendees?.existingAttendees?.[0]?.billingEmail
          ? [attendees.existingAttendees[0].billingEmail]
          : [];

      result.push({
        attendeesEmail,
        eventId,
        billingEmail,
        addeventIdentifier,
      });
    }

    return result;
  }

  const handlePaymentClick = async () => {
    setIsLoading(true);
    if (selectedPayment !== "card") {
      setIsLoading(false);
      toast.error("Please select card payment method");
      return;
    }
    const initialFormData = getInitialFormData(cart);
    try {
      const attendees = await addAttendee(
        initialFormData,
        dispatch,
        grandTotal,
      );
      if (
        !attendees.eventAttendees ||
        Object.keys(attendees.eventAttendees).length === 0
      ) {
        setIsLoading(false);
        toast.error(
          "Not enough seats available for event. Please try reducing your number of seats.",
        );
        return;
      }
      const data = transformData(
        attendees.eventAttendees,
        attendees?.addeventIdentifier,
      );
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/stripe/checkout/create-checkout-session`,
        { data, orderPrice: grandTotal },
      );
      setIsLoading(false);
      onClose();
      window.location.href = response.data.url;
    } catch (error) {
      setIsLoading(false);
      console.error("There was an error creating the payment link!", error);
      toast.error(
        "There was an error creating the payment link, please try again later.",
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-[#183864] to-[#B02526] rounded-full mb-4">
            <svg
              className="w-8 h-8 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
              />
            </svg>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
            Payment & Checkout
          </h1>
          <p className="text-gray-600 text-lg">
            Review your order and complete your registration
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Order Summary */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-[#183864] to-[#B02526] p-6 text-white">
              <h2 className="text-xl md:text-2xl font-bold mb-2">
                Order Summary
              </h2>
              <p className="text-blue-100">Review your selected classes</p>
            </div>

            <div className="p-6">
              <div className="space-y-4 mb-6">
                {cart.map((item, index) => (
                  <div key={index} className="bg-gray-50 rounded-xl p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-800 mb-1">
                          {item?.course?.name}
                        </h3>
                        <div className="flex flex-wrap gap-2 mb-2">
                          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                            {moment(item.event.startDate).format(
                              "MMM DD, YYYY",
                            )}
                          </span>
                          <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
                            {item.event.campus}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-gray-800">
                          ${item.course.price * item.seats}
                        </div>
                        <div className="text-sm text-gray-600">
                          {item.seats} × ${item.course.price}
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">
                        Tax ({item.course.tax || 5}%)
                      </span>
                      <span className="font-medium text-gray-800">
                        $
                        {(
                          item.course.price *
                          item.seats *
                          (parseFloat(item.course.tax || 5) / 100)
                        ).toFixed(2)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Price Breakdown */}
              <div className="border-t border-gray-200 pt-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-semibold text-gray-800">
                    ${totalPrice.toFixed(2)} CAD
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Tax</span>
                  <span className="font-semibold text-gray-800">
                    ${taxAmount.toFixed(2)} CAD
                  </span>
                </div>
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-gray-800">
                      Total Amount
                    </span>
                    <span className="text-2xl font-bold text-green-600">
                      ${grandTotal.toFixed(2)} CAD
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Method */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-[#183864] to-[#B02526] p-6 text-white">
              <h2 className="text-xl md:text-2xl font-bold mb-2">
                Payment Method
              </h2>
              <p className="text-green-100">
                Choose your preferred payment option
              </p>
            </div>

            <div className="p-6">
              <div className="space-y-4 mb-8">
                {/* Stripe Payment Option */}
                <div
                  className={`border-2 rounded-xl p-4 cursor-pointer transition-all duration-200 ${
                    selectedPayment === "card"
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => handlePaymentChange("card")}
                >
                  <div className="flex items-center space-x-4">
                    <input
                      type="radio"
                      value="card"
                      checked={selectedPayment === "card"}
                      onChange={() => handlePaymentChange("card")}
                      className="w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <img src={StripeImage} alt="Stripe" className="h-8" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-800">
                        Credit/Debit Card
                      </h3>
                      <p className="text-sm text-gray-600">
                        Secure payment via Stripe
                      </p>
                    </div>
                    <div className="flex space-x-1">
                      <div className="w-8 h-5 bg-blue-600 rounded text-white text-xs flex items-center justify-center font-bold">
                        V
                      </div>
                      <div className="w-8 h-5 bg-red-600 rounded text-white text-xs flex items-center justify-center font-bold">
                        M
                      </div>
                      <div className="w-8 h-5 bg-blue-800 rounded text-white text-xs flex items-center justify-center font-bold">
                        A
                      </div>
                    </div>
                  </div>
                </div>

                {/* PayPal Option (Disabled for now) */}
                <div className="border-2 border-gray-200 rounded-xl p-4 opacity-50 cursor-not-allowed">
                  <div className="flex items-center space-x-4">
                    <input
                      type="radio"
                      value="paypal"
                      disabled
                      className="w-5 h-5 text-blue-600 border-gray-300"
                    />
                    <img src={PaypalImage} alt="PayPal" className="h-8" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-800">PayPal</h3>
                      <p className="text-sm text-gray-600">Coming soon</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Security Notice */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <svg
                    className="w-5 h-5 text-green-600 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                  <div>
                    <h4 className="font-semibold text-green-800">
                      Secure Payment
                    </h4>
                    <p className="text-sm text-green-700">
                      Your payment information is encrypted and secure. We do
                      not store your card details.
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-4">
                <button
                  onClick={handlePaymentClick}
                  disabled={!selectedPayment || isLoading}
                  className={`w-full h-14 rounded-xl font-bold text-lg transition-all duration-200 flex items-center justify-center space-x-3 ${
                    selectedPayment && !isLoading
                      ? "bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
                      : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  {isLoading ? (
                    <>
                      <Loader />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span>
                        Complete Payment - ${grandTotal.toFixed(2)} CAD
                      </span>
                    </>
                  )}
                </button>

                <button
                  onClick={() => setStraper(4)}
                  className="w-full h-12 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-200 flex items-center justify-center space-x-2"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m0 0L12 21m0 0l2.5-3M12 21l2.5-3m2.5 0H16m1.5-3L19 16"
                    />
                  </svg>
                  <span>View Cart</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
